import { Box, Stack, Typography } from "@mui/material";
import { linko } from "@shared/linko";
import { useAdminEvent } from "@shared/frontend/hooks/use-admin-event";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FC } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { VoicenterCallButton } from "../voicenter/VoicenterCallButton";
import type { Admin } from "@shared/socket-namespaces/admin/admin.namespace";
import { CcButton } from "../../pages/call-center/CallCenterUi";
import { CC } from "../../pages/call-center/call-center-ui.theme";

const TYPE_COPY: Record<string, string> = {
  DISTRESS: "מצוקה",
  EARLY_LEAVE: "יציאה מוקדמת",
  CALL_COORDINATOR: "קריאה למנהלת",
};

export const CallCenterCoordinatorAlerts: FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [live, setLive] = useState<Admin.CallCenterAlertEvent | null>(null);

  useAdminEvent("call-center:alert", (data) => {
    setLive(data);
    void queryClient.invalidateQueries({ queryKey: ["call-center-alerts"] });
    void queryClient.invalidateQueries({ queryKey: ["call-center-shifts"] });
  });

  const openAlerts = useQuery({
    queryKey: ["call-center-alerts"],
    queryFn: async () => (await linko.callCenter.queries.listOpenAlerts()).data,
  });

  if (location.pathname.startsWith("/call-center-desk")) return null;

  const openCount = openAlerts.data?.length ?? 0;
  const fromList = openAlerts.data?.[0]
    ? {
        alertId: openAlerts.data[0].id,
        type: openAlerts.data[0].type,
        volunteerName: openAlerts.data[0].volunteer.name,
        stationNumber:
          openAlerts.data[0].shift?.stationManager?.stationNumber ?? null,
        opportunityTitle: null,
        opportunityId: openAlerts.data[0].opportunityId,
        shiftId: openAlerts.data[0].shiftId ?? "",
        volunteerId: openAlerts.data[0].volunteerId,
        reason: openAlerts.data[0].reason,
      }
    : null;
  const alert = live ?? fromList;

  if (!alert) return null;

  const distress = alert.type === "DISTRESS";

  return (
    <Box
      sx={{
        flexShrink: 0,
        zIndex: 30,
        px: 2,
        py: 0.75,
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        bgcolor: distress ? CC.distressSoft : CC.paper,
        color: CC.ink,
        borderBottom: `1px solid ${distress ? "rgba(180,35,24,0.22)" : CC.line}`,
        fontFamily: CC.font,
      }}
    >
      <Typography
        sx={{
          fontFamily: CC.font,
          fontWeight: 700,
          fontSize: 13,
          color: distress ? CC.distress : CC.ink,
          flexShrink: 0,
        }}
      >
        {TYPE_COPY[alert.type] ?? alert.type}
      </Typography>
      <Typography
        sx={{
          fontFamily: CC.font,
          flex: 1,
          minWidth: 0,
          fontSize: 14,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {alert.volunteerName}
        {alert.stationNumber != null ? ` · עמדה ${alert.stationNumber}` : ""}
        {alert.opportunityTitle ? ` · ${alert.opportunityTitle}` : ""}
        {alert.reason ? ` · ${alert.reason}` : ""}
        {openCount > 1 ? ` · +${openCount - 1} נוספות` : ""}
      </Typography>
      <Stack direction="row" gap={1} alignItems="center">
        {openAlerts.data?.[0]?.volunteer.phoneNumber ? (
          <VoicenterCallButton
            phoneNumber={openAlerts.data[0].volunteer.phoneNumber}
            compact
          />
        ) : null}
        <CcButton
          kind="ghost"
          size="small"
          onClick={() => navigate("/call-center-desk")}
        >
          למסך השליטה
        </CcButton>
        <CcButton
          kind={distress ? "danger" : "primary"}
          size="small"
          onClick={async () => {
            await linko.callCenter.mutations.resolveAlert({
              alertId: alert.alertId,
              approveLeave: alert.type === "EARLY_LEAVE",
            });
            setLive(null);
            void queryClient.invalidateQueries({
              queryKey: ["call-center-alerts"],
            });
          }}
        >
          {alert.type === "EARLY_LEAVE" ? "אישור מעבר" : "טופל"}
        </CcButton>
      </Stack>
    </Box>
  );
};
